import * as tokenStore from '@/utils/tokenStore';
import { clearMfaToken, endSupportTab, getAccessToken, getProfile, getRefreshToken, isSupportEnded, isSupportTab, removeProfile, setAccessToken, setProfile, setRefreshToken } from '@/utils/tokenStore';
import { createSupportRefresher, supportEndedUrl } from '@/utils/supportSession';
import { PERM_APP, diffSeenKey, menuItemAllowed, permUiDiffs } from '@/utils/permUi';
import { createStore } from "vuex";
import axios from "axios";
import { oneBtnDialog, jwtDecoder } from "../utils/utils";
import router from "@/router";
import i18n, { setLanguage } from "../i18n";
import { bridges } from "@/utils/bridges";
import manufacturerPromotions from "./modules/manufacturerPromotions";
import { isBlockedOnMobile } from "@/utils/mobileAccess";
import { RouterEnum } from "@/enums/router.enums";

// 지원 탭의 갱신(계약 SUPPORT 9): 쿠키·/auth/refresh 가 아니라 POST /auth/support/refresh, 동시에 와도 한 번만
const refreshSupportToken = createSupportRefresher({
  post: (url, body) => axios.post(`${process.env.VUE_APP_SERVERURL}${url}`, body),
  store: tokenStore,
});

/*
 * 폰 하단 탭의 전체 자리 수. 375px 에서 탭 하나에 최소 75px 이 필요하다.
 * 그중 한 자리는 마이페이지가 고정으로 쓰므로 제조사가 고를 수 있는 건 4개다
 * (백엔드 MAX_MOBILE_TABS 와 운영페이지 MAX_TABS 가 그 4를 쓴다).
 */
const MAX_MOBILE_TABS = 5;
// 읽는 중인 메뉴(privileges) {userId, promise} — ensurePrivileges 가 같은 계정 동시 요청을 합침
let privilegesLoading = null;

/**
 * 마이페이지.
 *
 * 항상 맨 끝에 붙고 잘리지 않는다. 폰에서 계정 정보를 보고 로그아웃하는
 * 유일한 경로라서, 제조사 설정이나 계정 권한과 무관하게 존재해야 한다.
 *
 * 아이콘은 전용 자산이 아직 없어 계정 아이콘을 임시로 쓴다.
 */
const buildMyPageTab = () => ({
  menuId: "__mypage__",
  // 다른 탭 이름은 서버가 회사 locale 에 맞춰 내려 준다. 이 탭만 코드에 있으므로 i18n 을 쓴다.
  menuName: i18n.global.t("navbar_mypage"),
  link: RouterEnum.MyPage.path,
  iconDefaultUrl: require("@/assets/img/sidenav/account.svg"),
  iconActiveUrl: require("@/assets/img/sidenav/account_a.svg"),
  isNewWindow: "N",
});

/**
 * 브라이트몰 주문 관리.
 *
 * menus 컬렉션에 등록돼 있지 않아 운영페이지의 탭 목록에 뜨지 않고
 * privileges 로도 내려오지 않는다. 등록하려면 덴티움 계정 1,255건의
 * privileges 를 전부 고쳐야 해서 지금까지 코드로 넣어 왔다.
 *
 * TODO: menus 에 등록되면 이 상수와 관련 분기를 지우고 일반 탭으로 처리한다.
 */
const BRIGHTMALL_TAB = {
  menuId: "__brightmall__",
  menuName: "주문 관리",
  link: `${RouterEnum.BrightmallManagement.path}/${RouterEnum.BrightmallManagement.Orders.path}`,
  /*
   * 아이콘은 origin/main 의 하단 탭에 있던 장바구니다.
   * 처음에는 contract_list.svg 를 임시로 썼는데 전자계약과 같은 문서 모양이라
   * 두 탭이 구분되지 않았다. 원래 쓰던 장바구니로 되돌렸다.
   *
   * URL 이 아니라 인라인인 이유는 이 모양의 자산 파일이 프로젝트에 없어서다.
   *
   * 비활성·활성 두 벌을 둔다. 서버가 주는 menus 아이콘이 비활성은 연하고
   * 활성은 속이 채워진 검은 모양으로 바뀌는데, 선 색만 바꾸면 이 탭만
   * 속이 빈 채로 남아 혼자 튄다. 색은 currentColor 라 탭 색을 그대로 따른다.
   */
  iconSvg:
    '<circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>' +
    '<path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>',
  iconSvgActive:
    '<path d="M6 6h17l-1.7 8.4a2 2 0 0 1-2 1.6h-9.7a2 2 0 0 1-2-1.6L6 6z" fill="currentColor" stroke="none"/>' +
    '<path d="M1 1h4l1 5" fill="none" stroke="currentColor"/>' +
    '<circle cx="9.5" cy="20" r="1.9" fill="currentColor" stroke="none"/>' +
    '<circle cx="19" cy="20" r="1.9" fill="currentColor" stroke="none"/>',
  isNewWindow: "N",
};

export default createStore({
  modules: {
    manufacturerPromotions,
  },
  state: {
    showSidenav: true,
    showNavbar: true,
    showFooter: true,
    userName: "",
    manager: "",
    company: "",
    companyImageUrl: "",
    accessToken: "",
    refreshToken: "",
    isLoading: false,
    userInfo: {
      username: "",
      imgUrl: "",
      companyName: "",
      userId: "",
      locale: "",
    },
    privileges: {},
    /*
     * 앱 번들 버전. 앱 웹뷰 안에서만 값이 들어온다.
     *
     * 웹은 자기 힘으로 알 수 없어서, 앱이 페이지 로드를 마친 뒤
     * window.setAppVersion 을 부른다(App.vue 에서 바인딩 → 여기로 commit).
     * 마이페이지의 '버전 정보' 행이 이 값을 보고 나타난다 — 비어 있으면 행이 없다.
     *
     * 전역 변수가 아니라 store 에 두는 이유: 앱이 언제 부를지 화면이 알 수 없다.
     * 마이페이지에 있는 상태로 새로고침되면 컴포넌트가 먼저 만들어질 수 있는데,
     * 그때 전역 변수를 한 번만 읽으면 값이 없는 채로 굳어 행이 영영 안 나온다.
     */
    appVersion: null,
    // 제조사별 모바일 하단 탭 구성. 운영페이지에서 정한다.
    // 켜고 끄는 플래그는 없다 — 목록이 비어 있으면 그 제조사의 탭이 없는 것이다.
    mobileTabs: { tabs: [] },
    // 어느 회사 것을 받아 뒀는지. 로그아웃해도 store 는 초기화되지 않으므로
    // (clearLocalStorage 는 localStorage 만 지운다) 이 값이 없으면
    // 다른 계정으로 다시 로그인했을 때 이전 제조사의 탭이 그대로 남는다.
    mobileTabsCompanyId: null,
    primaryChannel: null,
    primaryChannelLoaded: false,
    // GET /auth/introspect 의 권한·모드(계약 PERMISSIONS 7). 응답에 칸이 없으면(옛 inapi) 화면은 옛 규칙
    introspect: null,
    // 라우터가 「아직 안 읽음」(기다림)과 「읽다 실패」(막지 않음)를 가르는 표시 — 메뉴는 계정마다(로그아웃해도 store 는 남음)
    introspectTried: false,
    privilegesTried: { userId: null, ok: false },
    // GET /perm-catalog 이름표 {코드: ko} — 403 PERMISSION_DENIED 문구용(못 받으면 코드 그대로)
    permLabels: {},
  },
  mutations: {
    setIsLoading(state, input) {
      state.isLoading = input;
    },
    // 앱이 window.setAppVersion 으로 알려 준 번들 버전을 담는다(App.vue 에서 commit).
    setAppVersion(state, input) {
      state.appVersion = input || null;
    },
    forceSignOut({ commit }) {
      localStorage.clear();
      window.reload();
    },
    setUserInfo(state, payload) {
      state.userInfo = payload;

      if (payload.userName) state.userName = payload.userName;
      if (payload.manager) state.manager = payload.manager;
      if (payload.company) state.company = payload.company;
      if (payload.companyImageUrl)
        state.companyImageUrl = payload.companyImageUrl;
      // locale은 i18n+localStorage 동기화 헬퍼로 일원화 (sidenav 직접 변경 안티패턴 제거)
      if (payload.locale) {
        setLanguage(payload.locale);
      } else {
        try {
          removeProfile("locale");
        } catch (e) {
          // localStorage 접근 실패는 무시
        }
      }
    },
    mutateClearLocalStorage(state) {
      // 지원 탭: 지원 끝(래치) — 그 탭 지원 값만 지우고 일반 로그인 localStorage 는 건드리지 않음
      if (isSupportTab()) return endSupportTab("ENDED");
      setAccessToken(null);
      setRefreshToken(null);
      clearMfaToken();
      removeProfile("nameManu");
      removeProfile("managerManu");
      removeProfile("companyManu");
      removeProfile("companyImageUrlManu");
      removeProfile("CompanyInfo");
      removeProfile("companyImageUrlManu");
    },
    setPrivilegesInfo(state, payload) {
      state.privileges = payload;
    },
    setMobileTabs(state, payload) {
      state.mobileTabs = { tabs: payload.tabs };
      state.mobileTabsCompanyId = payload.companyId ?? null;
    },
    setIntrospect(state, data) {
      state.introspect = data || null;
      state.introspectTried = true;
    },
    setPrivilegesTried(state, payload) {
      state.privilegesTried = payload;
    },
    setPermLabels(state, labels) {
      state.permLabels = labels || {};
    },
    setPrimaryChannel(state, channel) {
      state.primaryChannel = channel;
      state.primaryChannelLoaded = true;
    },
    clearPrimaryChannel(state) {
      state.primaryChannel = null;
      state.primaryChannelLoaded = false;
    },
  },
  actions: {
    clearLocalStorage({ commit }) {
      if (isSupportTab()) return endSupportTab("USER");
      setAccessToken(null);
      setRefreshToken(null);
      clearMfaToken();
      removeProfile("nameManu");
      removeProfile("managerManu");
      removeProfile("companyManu");
      removeProfile("companyImageUrlManu");
      removeProfile("CompanyInfo");
      removeProfile("manufacturerNameManu");
    },
    async getCompanyInfo({ commit }, root) {
      const companyId = jwtDecoder(getAccessToken());
      if (companyId) {
        try {
          // $apiInstance: 만료 토큰이면 재발행(폰은 mobileT) 뒤 다시 보낸다(main.js 에서 꽂아 둠)
          const response = await this.$apiInstance.get(`/companies/${companyId}`);
          if (response.status === 200 || response.status === 201) {
            const { data } = response;

            // CompanyInfo가 없으면 저장 (토큰 기반 로그인 대응)
            if (!getProfile("CompanyInfo")) {
              setProfile("CompanyInfo", JSON.stringify(data));
            }

            const userInfo = {};
            userInfo.username = data.accountInfo.name;
            userInfo.imgUrl = data.imgUrl;
            userInfo.companyName = data.name;
            userInfo.userId = data.accountInfo.username;
            userInfo.locale = data.locale;
            commit("setUserInfo", userInfo);
          }
        } catch (error) {
          console.log(error);
          if (root && root.$i18n) {
            await oneBtnDialog(
              root.$i18n.t("get_comanyinfo_error_title"),
              root.$i18n.t("get_comanyinfo_error_content"),
              false
            );
          }
        }
      }
    },
    async getPrivileges({ commit }, userId, root) {
      const companyId = jwtDecoder(getAccessToken());
      if (!companyId) return;
      try {
        if (!userId) return;
        // 앱 실행 직후 폰에 남은 만료 토큰으로 나가므로 재발행을 거치는 $apiInstance 로 보낸다
        const response = await this.$apiInstance.get(`/privileges/${userId}`);
        if (response.status === 200 || response.status === 201) {
          if (response.data.length === 0) {
            commit("setPrivilegesInfo", {
              category: [],
              username: "",
            });
            commit("setPrivilegesTried", { userId, ok: true });
            return;
          }
          const newArray = response.data.categories.filter(
            (item) => item.menu.length > 0
          );
          commit("setPrivilegesInfo", {
            category: newArray,
            username: response.data.username,
          });
          commit("setPrivilegesTried", { userId, ok: true });
        }
      } catch (error) {
        // 팝업은 부른 쪽이 정한다. 여기서 띄우면 모바일 진입(signInMixin·MobileLanding)의
        // .catch 로 조용히 넘기려던 처리가 먹히지 않아, 만료 토큰 등으로 실패할 때 폰에 팝업이 뜬다.
        console.log(error);
        throw error;
      }
    },
    /**
     * 라우터 가드·사이드바처럼 첫 화면에서 같이 부르는 쪽: 같은 계정 메뉴를 동시에 두 번 읽지 않음(실패 창도 한 번).
     * 끝났는데 성공 표시가 없으면(던졌든 삼켰든) 「읽다 실패」 — 라우터는 이 계정 메뉴로 막지 않고 이동마다 다시 읽지 않음.
     * 실패는 그대로 다시 던짐(부른 쪽 처리는 getPrivileges 를 부를 때와 같게). getPrivileges 본문은 원래 모양 — 다른 호출·다른 팀 시험이 그대로 부름
     */
    ensurePrivileges({ commit, dispatch, state }, userId) {
      if (privilegesLoading && privilegesLoading.userId === userId) return privilegesLoading.promise;
      const markFailed = () => {
        if (userId && state.privilegesTried.userId !== userId) commit("setPrivilegesTried", { userId, ok: false });
      };
      const promise = dispatch("getPrivileges", userId)
        .then(markFailed, (error) => {
          markFailed();
          throw error;
        })
        .finally(() => {
          if (privilegesLoading?.promise === promise) privilegesLoading = null;
        });
      privilegesLoading = { userId, promise };
      return promise;
    },
    /**
     * 제조사별 모바일 하단 탭 구성을 받아 둔다.
     *
     * 서버가 menuId 를 menus 와 조인해 link·메뉴명·아이콘까지 붙여 주므로
     * 여기서는 그대로 보관하고, 실제 노출은 MobileTabBar 가 privileges 와
     * 교집합을 내서 정한다.
     *
     * 실패해도 조용히 넘어간다 — 하단 탭은 부가 기능이고,
     * 여기서 오류 팝업을 띄우면 설정이 없는 제조사 전부가 로그인마다 팝업을 본다.
     */
    async getMobileTabs({ commit }) {
      const companyId = jwtDecoder(getAccessToken());
      if (!companyId) return;
      try {
        const response = await this.$apiInstance.get(`/company-mobile-tabs/${companyId}`);
        if (response.status === 200 || response.status === 201) {
          const body = response.data || {};
          commit("setMobileTabs", {
            companyId,
            tabs: Array.isArray(body.tabs) ? body.tabs : [],
          });
          return;
        }
        commit("setMobileTabs", { companyId, tabs: [] });
      } catch (error) {
        console.log(error);
        // 실패해도 '사용 안 함'으로 확정해 둔다. companyId 를 같이 남겨야
        // 같은 계정에서 이동할 때마다 요청이 반복되지 않는다.
        commit("setMobileTabs", { companyId, tabs: [] });
      }
    },
    /**
     * 지원 접속 끝(계약 SUPPORT 9): 사용자가 끝내면(reason 없음 = USER) POST /auth/support/end(실패해도 진행) →
     * 그 탭 지원 저장값만 지우고 끝 화면으로 새로 고침(store·화면 상태를 비움). 서버가 끝낸 경우는 사유만 보임.
     */
    async endSupport(_, reason = "USER") {
      if (reason === "USER" && isSupportTab()) {
        try {
          await axios.post(`${process.env.VUE_APP_SERVERURL}/auth/support/end`, null, {
            headers: { Authorization: `Bearer ${getAccessToken()}` },
          });
        } catch (error) {
          // 이미 끝난 세션(401)이어도 끝 화면으로
        }
      }
      // 래치: 페이지가 내려가기 전까지도 이 탭은 지원 탭(토큰 null) — 일반 로그인 토큰으로 다시 보내지 않게
      endSupportTab(reason);
      window.location.replace(router.resolve(supportEndedUrl(reason)).href);
    },
    /** 권한 값·모드 읽기(403 PERMISSION_DENIED 뒤 한 번 다시 — axios reloadPermsOnce). 실패하면 옛 규칙 그대로 */
    async loadIntrospect({ commit, dispatch }) {
      if (!getAccessToken()) return;
      try {
        const { data } = await axios.get(`${process.env.VUE_APP_SERVERURL}/auth/introspect`, {
          headers: { Authorization: `Bearer ${getAccessToken()}` },
        });
        commit("setIntrospect", data);
        dispatch("loadPermLabels");
      } catch (error) {
        commit("setIntrospect", null);
      }
    },
    async loadPermLabels({ commit, state }) {
      if (Object.keys(state.permLabels).length) return;
      try {
        const { data } = await axios.get(`${process.env.VUE_APP_SERVERURL}/perm-catalog`, {
          params: { app: PERM_APP },
          headers: { Authorization: `Bearer ${getAccessToken()}` },
        });
        commit("setPermLabels", Object.fromEntries((data?.codes || []).map((c) => [c.code, c.ko])));
      } catch (error) {
        // 이름표가 없으면 코드 그대로 보임
      }
    },
    /**
     * 옛 규칙과 can() 이 다른 메뉴를 POST /perm/ui-diff 로 보고(계약 PERMISSIONS 7 — log 동안, 화면은 바꾸지 않음).
     * 계정·코드마다 한 번(localStorage 표시 — 소음 줄이기라 비어도 괜찮음).
     */
    async reportPermUiDiff({ state }, { legacyByKey, table }) {
      const perms = state.introspect?.profile?.perms;
      const accountId = state.introspect?.accountId;
      for (const body of permUiDiffs({ legacyByKey, perms }, table)) {
        const seen = diffSeenKey(accountId, body.code);
        try {
          if (localStorage.getItem(seen)) continue;
          localStorage.setItem(seen, "1");
        } catch (e) {
          // 저장소를 못 써도 보고는 함
        }
        axios
          .post(`${process.env.VUE_APP_SERVERURL}/perm/ui-diff`, body, {
            headers: { Authorization: `Bearer ${getAccessToken()}` },
          })
          .catch(() => {});
      }
    },
    async requestAccessToken({ commit, dispatch }) {
      if (isSupportTab()) {
        // 이미 끝난 지원 탭: 갱신하지 않음(부른 axios 가 원래 요청을 실패로 끝냄)
        if (isSupportEnded()) return;
        // 지원 탭: 실패하면 갱신을 반복하지 않고 끝 화면(모바일 재발급·앱 다리도 쓰지 않음)
        const result = await refreshSupportToken();
        if (result.ended) await dispatch("endSupport", result.ended);
        return;
      }
      await axios
        .post(`${process.env.VUE_APP_SERVERURL}/auth/refresh`, null, {
          headers: {
            Authorization: `Bearer ${getRefreshToken()}`,
          },
        })
        .then((response) => {
          if (response.status === 200 || response.status === 201) {
            this.state.accessToken = response.data.accessToken;
            this.state.refreshToken = response.data.refreshToken;
            setAccessToken(response.data.accessToken);
            setRefreshToken(response.data.refreshToken);
            console.log("token issued");
            bridges().webToApp(response.data);
          } else {
            console.log("token not issued");
            bridges().webToApp({ success: false });
            return;
          }
        })
        .catch(async (error) => {
          console.log(error);
          // if(error.response && (error.response.status === 403 || error.response.status === 401)) {
          //   commit('mutateClearLocalStorage');
          //   await oneBtnDialog('토큰 만료','토큰이 만료되었습니다. 다시 로그인해주세요.', false);
          //   if (error.response.status === 401) {
          //     window.location.href = '/signin';
          //   }
          // }
          //console.log(window.innerWidth)

          if (
            error.response &&
            (error.response.status === 401 || error.response.status === 403)
          ) {
            if (window.innerWidth < 768) {
              try {
                const headers = {};
                headers["fbd-tkn"] = getAccessToken();
                const response = await axios.post(
                  `${process.env.VUE_APP_SERVERURL}/auth/generate/mobileT`,
                  {},
                  { headers }
                );
                if (response.data?.accessToken) {
                  setAccessToken(response.data.accessToken);
                  if (response.data.refreshToken) {
                    setRefreshToken(response.data.refreshToken);
                  }
                  return;
                }
              } catch (mobileError) {
                // 폰 재발행도 실패(서명 불일치·다른 환경 토큰·국외 등) → 아래 PC 와 같이 재로그인 안내
                console.log(mobileError);
              }
            }
            bridges().webToApp({ success: false });
            commit("mutateClearLocalStorage");
            if (window.innerWidth < 768) {
              /*
               * 폰은 안내 팝업 없이 바로 로그인 화면으로 보낸다.
               *
               * 앱 진입 경유 화면(MobileLanding)이 전체 스피너를 켠 채 진입 요청을 기다리는데,
               * 스피너(z-index 9998)가 팝업(1060)을 덮어 「확인」을 누를 수 없다.
               * 여기서 팝업을 기다리면 재발행 → 진입 요청 → 경유 화면이 모두 끝나지 않아
               * 「토큰 만료」 팝업 위에서 스피너가 계속 돈다(서명이 맞지 않는 토큰으로 재현, 2026-10-28).
               * 이동하면 페이지가 새로 떠 스피너도 사라진다.
               */
              window.location.href = "/signin";
              throw error;
            }
            await oneBtnDialog(
              "토큰 만료",
              "토큰이 만료되었습니다. 다시 로그인해주세요.",
              false
            );
            if (error.response.status === 401) {
              window.location.href = "/signin";
            }
            // 재발행 실패를 알려야 axios 인터셉터가 기다리던 요청을 다시 보내지 않는다(안내 중복 방지)
            throw error;
          }
        });
    },
    async fetchPrimaryChannel({ commit, state }) {
      if (state.primaryChannelLoaded) {
        return state.primaryChannel;
      }
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_SERVERURL}/companies/alimtalk-channel/primary`,
          {
            headers: {
              Authorization: `Bearer ${getAccessToken()}`,
            },
          }
        );
        if (response.status === 200 || response.status === 201) {
          const channel = response.data?.success && response.data?.data ? response.data.data : null;
          commit("setPrimaryChannel", channel);
          return channel;
        }
      } catch (error) {
        console.error("Failed to fetch primary channel:", error);
        commit("setPrimaryChannel", null);
      }
      return null;
    },
    resetPrimaryChannel({ commit }) {
      commit("clearPrimaryChannel");
    },
    setUserInfo({ commit }, payload) {
      commit("setUserInfo", payload);
      if (payload.userName) {
        setProfile("nameManu", payload.userName);
      }
      if (payload.manager) {
        setProfile("managerManu", payload.manager);
      }
      if (payload.company) {
        setProfile("companyManu", payload.company);
      }
      if (payload.companyImageUrl) {
        setProfile("companyImageUrlManu", payload.companyImageUrl);
      }
    },
    // locale 변경 단일 진입점 (컴포넌트가 $i18n.locale을 직접 변경하지 않도록)
    // setUserInfo mutation을 재사용하여 i18n+localStorage 동기화까지 일원화
    setLocale({ commit, state }, locale) {
      commit("setUserInfo", { ...state.userInfo, locale });
    },
  },
  getters: {
    getUserName: (state) => state.userName,
    getManager: (state) => state.manager,
    getCompany: (state) => state.company,
    getCompanyImageUrl: (state) => state.companyImageUrl,
    getUserInfo: (state) => state.userInfo,
    getPrivilegesInfo: (state) => state.privileges,
    getMobileTabs: (state) => state.mobileTabs,
    // 어느 회사 설정을 들고 있는지. App.vue 가 현재 토큰의 회사와 비교해
    // 다르면 다시 받는다 — 계정 전환 시 이전 제조사 탭이 남지 않게 한다.
    getMobileTabsCompanyId: (state) => state.mobileTabsCompanyId,
    /**
     * 폰에 실제로 그릴 탭.
     *
     *   [제조사가 고른 탭 ∩ 계정 권한]  +  [브라이트몰(덴티움)]  +  [마이페이지(고정)]
     *
     * 여기 한 곳에서만 계산한다. MobileTabBar 는 그리기만 하고,
     * App.vue 는 본문 아래 여백을 줄지 판단하는 데 쓴다.
     * 두 곳이 각자 계산하면 '바는 없는데 여백만 남는' 어긋남이 생긴다.
     *
     * 브라이트몰은 menus 에 없어 제조사가 고를 수 없다. 그래서 제조사 설정과
     * 무관하게, 회사 조건(isDentium)만으로 붙인다 — 예전 하단 탭이 하던 그대로다.
     * 설정을 안 한 제조사라고 해서 덴티움이 주문 관리를 잃으면 안 된다.
     *
     * 마이페이지는 자르기 전에 자리를 확보한다. 앞의 것들이 넘치면 앞쪽이 잘리고
     * 마이페이지는 남는다 — 로그아웃 경로가 사라지면 안 되기 때문이다.
     */
    getVisibleMobileTabs: (state) => {
      const conf = state.mobileTabs;

      const allowed = new Set();
      const categories = Array.isArray(state.privileges?.category)
        ? state.privileges.category
        : [];
      categories.forEach((c) => {
        (c?.menu || []).forEach((m) => {
          if (m?.link) allowed.add(m.link);
        });
      });

      // 제조사가 아직 안 골랐으면 빈 목록이다. 브라이트몰·마이페이지는 아래에서 따로 붙는다.
      // 새 권한(enforce 날)도 사이드바·라우터와 같은 판정 — 탭에 보이는데 누르면 튕기지 않게
      const intro = state.introspect;
      const picked = (conf?.tabs || [])
        .filter((t) => t?.link)
        .filter((t) => !isBlockedOnMobile(t.link))
        .filter((t) => allowed.has(t.link))
        .filter((t) => menuItemAllowed(t, { perms: intro?.profile?.perms, modes: intro?.modes }));

      let isDentium = false;
      try {
        isDentium =
          jwtDecoder(getAccessToken()) ===
          process.env.VUE_APP_DENTIUM_ID;
      } catch (e) {
        isDentium = false;
      }
      // 마이페이지 자리(1)를 남겨 두고 붙인다.
      if (isDentium && picked.length < MAX_MOBILE_TABS - 1) {
        picked.push(BRIGHTMALL_TAB);
      }

      // 마이페이지는 맨 끝에, 잘리지 않게. 앞쪽이 넘치면 앞쪽을 자른다.
      return [...picked.slice(0, MAX_MOBILE_TABS - 1), buildMyPageTab()];
    },
    getLocale: (state) => state.userInfo.locale,
  },
});
